import React from 'react';
import { X, Globe, Mail, Phone, MapPin, Shield, Server, BarChart2 } from 'lucide-react';
import Button from './components/Button';

export const ImpressumModal = ({ onClose }) => (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto transform transition-all scale-100">
            <div className="sticky top-0 bg-white dark:bg-zinc-900 border-b border-gray-100 dark:border-zinc-800 p-4 flex justify-between items-center z-10">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                    <Globe className="w-5 h-5 text-accent-mint" />
                    Impressum
                </h2>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500"
                >
                    <X className="w-5 h-5" />
                </Button>
            </div>

            <div className="p-6 space-y-6 text-gray-600 dark:text-slate-300">
                <section>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Angaben gemäß § 5 TMG</h3>
                    <p className="font-medium">Christos Kessopoulos</p>
                    <p>ell.in Grill & Pizzeria</p>
                    <p>Einzelunternehmer</p>
                    <div className="mt-2 flex items-start gap-2">
                        <MapPin className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                        <p>Mühlenstr. 2<br />41352 Korschenbroich</p>
                    </div>
                </section>

                <section>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Kontakt</h3>
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <Phone className="w-5 h-5 text-gray-400" />
                            <a href="tel:021614754454" className="hover:text-accent-mint transition-colors">02161 4754 454</a>
                        </div>
                        <div className="flex items-center gap-3">
                            <Mail className="w-5 h-5 text-gray-400" />
                            <a href="mailto:info@ellin-grill.de" className="hover:text-accent-mint transition-colors">info@ellin-grill.de</a>
                        </div>
                    </div>
                </section>

                <section className="text-sm text-gray-500 border-t pt-4 dark:border-zinc-800">
                    <h4 className="font-semibold mb-2">Streitschlichtung</h4>
                    <p>
                        Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
                        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-accent-mint hover:underline mx-1">
                            https://ec.europa.eu/consumers/odr
                        </a>.<br />
                        Unsere E-Mail-Adresse finden Sie oben im Impressum.
                    </p>
                    <p className="mt-2">
                        Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
                    </p>
                </section>
            </div>
        </div>
    </div>
);

export const DatenschutzModal = ({ onClose }) => (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto transform transition-all scale-100">
            <div className="sticky top-0 bg-white dark:bg-zinc-900 border-b border-gray-100 dark:border-zinc-800 p-4 flex justify-between items-center z-10">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-accent-mint" />
                    Datenschutzerklärung
                </h2>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500"
                >
                    <X className="w-5 h-5" />
                </Button>
            </div>

            <div className="p-6 space-y-6 text-gray-600 dark:text-slate-300 text-sm leading-relaxed">
                <section>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">1. Datenschutz auf einen Blick</h3>
                    <p><strong>Allgemeine Hinweise:</strong> Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese App nutzen.</p>
                </section>

                <section>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">2. Verantwortliche Stelle</h3>
                    <p>Verantwortlich für die Datenverarbeitung ist:</p>
                    <div className="mt-2 p-3 bg-gray-50 dark:bg-zinc-800/50 rounded-lg">
                        <p className="font-medium text-gray-900 dark:text-white">Christos Kessopoulos</p>
                        <p>ell.in Grill & Pizzeria</p>
                        <p>Mühlenstr. 2, 41352 Korschenbroich</p>
                        <p>E-Mail: info@ellin-grill.de</p>
                    </div>
                </section>

                <section>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                        <Server className="w-4 h-4" />
                        3. Hosting und Datenspeicherung
                    </h3>
                    <p className="mb-2">Wir hosten die Inhalte unserer App bei folgendem Anbieter:</p>
                    <ul className="list-disc ml-5 space-y-1">
                        <li><strong>Google Firebase (Google Ireland Limited)</strong></li>
                        <li>Gordon House, Barrow Street, Dublin 4, Irland</li>
                    </ul>
                    <p className="mt-2">
                        Firebase wird verwendet für:
                        <br />- <strong>Firestore Database:</strong> Speicherung der Arbeitszeiten und Mitarbeiterdaten.
                        <br />- <strong>Authentication:</strong> Sichere Anmeldung der Benutzer.
                        <br />- <strong>Hosting:</strong> Bereitstellung der App.
                    </p>
                    <p className="mt-2 text-xs text-gray-500">
                        Die Datenübertragung in die USA wird auf die Standardvertragsklauseln der EU-Kommission gestützt.
                        Details finden Sie hier: <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" className="text-accent-mint hover:underline">https://firebase.google.com/support/privacy</a>
                    </p>
                </section>

                <section>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                        <BarChart2 className="w-4 h-4" />
                        4. Analyse-Tools
                    </h3>
                    <p>Diese App nutzt <strong>Google Analytics für Firebase</strong>, um die Nutzung der App zu analysieren und Fehler zu beheben.</p>
                    <p className="mt-1">
                        Anbieter ist ebenfalls Google Ireland Limited. Google Analytics verwendet Technologien wie Cookies oder Device-Fingerprinting, um eine Analyse des Nutzerverhaltens zu ermöglichen.
                        Die erfassten Informationen werden in der Regel an einen Server von Google in den USA übertragen und dort gespeichert.
                    </p>
                </section>

                <section>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">5. Erhebung von Daten</h3>
                    <p>Wir erheben und verarbeiten folgende Daten zur Erfüllung des Arbeitsverhältnisses (Zeiterfassung):</p>
                    <ul className="list-disc ml-5 mt-2 space-y-1">
                        <li>Stammdaten (Name, Vorname, Benutzername)</li>
                        <li>Kontaktdaten (E-Mail, optional)</li>
                        <li>Arbeitszeiten (Datum, Start, Ende, Pausen)</li>
                        <li>Systemdaten (IP-Adresse, Login-Zeitpunkte - zur Sicherheit via Firebase Auth)</li>
                    </ul>
                </section>

                <section>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">6. Ihre Rechte</h3>
                    <p>Sie haben jederzeit das Recht auf:</p>
                    <ul className="list-disc ml-5 mt-2 space-y-1">
                        <li>Auskunft über Ihre gespeicherten Daten (Art. 15 DSGVO)</li>
                        <li>Berichtigung falscher Daten (Art. 16 DSGVO)</li>
                        <li>Löschung Ihrer Daten (Art. 17 DSGVO), sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen</li>
                        <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
                    </ul>
                    <p className="mt-2">Bitte wenden Sie sich hierfür an die oben genannte verantwortliche Stelle.</p>
                </section>
            </div>
        </div>
    </div>
);
